/**
 * The product's vocabulary, in one place.
 *
 * ALLOW · DENY · ESCALATE · CHALLENGE are not statuses, they are the four answers this product
 * gives, and the design language makes them the palette. A second copy of this mapping is how
 * `challenge` starts rendering like a denial somewhere — which is the single easiest mistake
 * to make against this API, and the reason the SDK gives it its own exception type rather than
 * a subclass of the denial one.
 */

export const VERDICTS = ["allow", "deny", "escalate", "challenge", "metered"] as const;

export type Verdict = (typeof VERDICTS)[number];

/** Normalise whatever the API said into one of the five. Unknown reads as `metered`, which is
 *  the one neutral value — guessing `allow` would paint an unrecognised verdict green. */
export function asVerdict(value: string | null | undefined): Verdict {
  const v = (value ?? "").toLowerCase().trim();
  return (VERDICTS as readonly string[]).includes(v) ? (v as Verdict) : "metered";
}

/** What each one means, in the operator's words. Used for title text and empty states. */
export const VERDICT_MEANING: Record<Verdict, string> = {
  allow: "Within mandate, within budget, within scope. It proceeded — and was signed anyway.",
  deny: "It stopped, with a reason code you can debug from.",
  escalate: "A named human must sign, with their own key. The agent is waiting.",
  challenge: "Not yet — and here is what would change that. The agent can step up and retry.",
  metered: "Recorded and counted. No gate applied.",
};
